import { useState } from "react";
import { Search, Users, Star, ChevronDown, Calendar, MapPin, Building2, Bell, BellOff, Briefcase, Globe, ExternalLink, TrendingUp, ArrowLeft } from "lucide-react";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { useToast } from "../../hooks/use-toast";

const industries = ["All Industries", "Oil & Gas", "Banking & Finance", "Technology", "Telecom", "Consulting", "Retail", "Education"];

const allCompanies = [
{ id: 1, name: "SOCAR", industry: "Oil & Gas", bio: "State Oil Company of Azerbaijan Republic. Largest company in Azerbaijan, operating in oil and gas exploration, production, and refining.", employees: "50,000+", initials: "SO", rating: 4.6, isFollowing: true, events: [
  { title: "SOCAR Graduate Program 2026", date: "Apr 1, 2026", location: "Baku HQ", attendees: 200, type: "Career" },
  { title: "Energy Innovation Summit", date: "May 10, 2026", location: "Heydar Aliyev Center", attendees: 500, type: "Conference" }],
  recentPosts: 5 },
{ id: 2, name: "Kapital Bank", industry: "Banking & Finance", bio: "One of the leading banks in Azerbaijan providing retail and corporate banking services, digital banking solutions, and financial advisory.", employees: "3,000+", initials: "KB", rating: 4.5, isFollowing: false, events: [
  { title: "Fintech Hackathon 2026", date: "Mar 25, 2026", location: "Kapital Bank HQ", attendees: 80, type: "Hackathon" }],
  recentPosts: 3 },
{ id: 3, name: "BP Azerbaijan", industry: "Oil & Gas", bio: "Global energy company operating major oil and gas projects in Azerbaijan, including the ACG field and BTC pipeline.", employees: "2,500+", initials: "BP", rating: 4.8, isFollowing: true, events: [
  { title: "BP Internship Fair", date: "Mar 30, 2026", location: "Villa Petrolea", attendees: 150, type: "Career" },
  { title: "Sustainability Workshop", date: "Apr 15, 2026", location: "ADA University", attendees: 60, type: "Workshop" }],
  recentPosts: 8 },
{ id: 4, name: "Azercell", industry: "Telecom", bio: "Leading mobile operator in Azerbaijan with over 5 million subscribers. Pioneer in digital services and 5G innovation.", employees: "1,500+", initials: "AZ", rating: 4.3, isFollowing: false, events: [
  { title: "5G Innovation Lab Tour", date: "Apr 5, 2026", location: "Azercell Office", attendees: 30, type: "Workshop" }],
  recentPosts: 2 },
{ id: 5, name: "McKinsey Baku", industry: "Consulting", bio: "Global management consulting firm helping organizations solve complex problems and drive sustainable growth.", employees: "100+", initials: "MK", rating: 4.9, isFollowing: false, events: [
  { title: "Case Interview Prep", date: "Mar 20, 2026", location: "Online", attendees: 120, type: "Career" }],
  recentPosts: 1 },
{ id: 6, name: "Bravo Supermarket", industry: "Retail", bio: "Largest supermarket chain in Azerbaijan with focus on quality products and excellent customer service.", employees: "5,000+", initials: "BR", rating: 4.1, isFollowing: false, events: [], recentPosts: 0 },
{ id: 7, name: "ADA University", industry: "Education", bio: "Leading private university in Azerbaijan focused on public policy, IT, engineering, and business education.", employees: "500+", initials: "AD", rating: 4.7, isFollowing: true, events: [
  { title: "Open Day 2026", date: "Apr 12, 2026", location: "ADA Campus", attendees: 300, type: "Career" },
  { title: "Tech Startup Demo Day", date: "Apr 20, 2026", location: "Innovation Center", attendees: 100, type: "Conference" }],
  recentPosts: 12 },
{ id: 8, name: "Pasha Holding", industry: "Banking & Finance", bio: "Diversified conglomerate with operations in banking, insurance, real estate, and construction across the Caucasus.", employees: "4,000+", initials: "PH", rating: 4.4, isFollowing: false, events: [
  { title: "Young Leaders Program", date: "May 1, 2026", location: "Pasha HQ", attendees: 50, type: "Career" }],
  recentPosts: 4 }];


const eventTypes = ["All Events", "Career", "Hackathon", "Workshop", "Conference"];

const CompaniesDiscovery = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIndustry, setSelectedIndustry] = useState("All Industries");
  const [searchMode, setSearchMode] = useState("companies");
  const [selectedEventType, setSelectedEventType] = useState("All Events");
  const [showRecommended, setShowRecommended] = useState(false);
  const [following, setFollowing] = useState([1, 3, 7]);
  const [notifications, setNotifications] = useState({ 1: true, 3: true, 7: true });
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const { toast } = useToast();

  const filtered = allCompanies.filter((c) => {
    const matchesSearch = c.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesIndustry = selectedIndustry === "All Industries" || c.industry === selectedIndustry;
    const matchesRecommended = !showRecommended || following.includes(c.id) || c.rating >= 4.5;
    return matchesSearch && matchesIndustry && matchesRecommended;
  });

  const allEvents = allCompanies.flatMap((company) =>
  company.events.map((e) => ({ ...e, companyName: company.name, companyId: company.id, companyInitials: company.initials, industry: company.industry }))
  );

  const filteredEvents = allEvents.filter((e) => {
    const matchesSearch = searchQuery.length < 2 || e.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesIndustry = selectedIndustry === "All Industries" || e.industry === selectedIndustry;
    const matchesType = selectedEventType === "All Events" || e.type === selectedEventType;
    const matchesRecommended = !showRecommended || following.includes(e.companyId);
    return matchesSearch && matchesIndustry && matchesType && matchesRecommended;
  });

  const toggleFollow = (id, name) => {
    const isFollowed = following.includes(id);
    setFollowing((prev) => isFollowed ? prev.filter((f) => f !== id) : [...prev, id]);
    if (!isFollowed) {
      setNotifications((prev) => ({ ...prev, [id]: true }));
      toast({ title: `Following ${name}`, description: "You'll receive updates when they share new content." });
    } else {
      setNotifications((prev) => {const n = { ...prev };delete n[id];return n;});
      toast({ title: `Unfollowed ${name}` });
    }
  };

  const toggleNotification = (id, e) => {
    e.stopPropagation();
    setNotifications((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const selectedCompany = selectedCompanyId ? allCompanies.find((c) => c.id === selectedCompanyId) : null;

  // Company Detail View
  if (selectedCompany) {
    const isFollowed = following.includes(selectedCompany.id);
    return (
      <div className="space-y-4">
        <div className="stat-card !p-5">
          <button onClick={() => setSelectedCompanyId(null)} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors mb-3">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Companies
          </button>
          <div className="flex items-start gap-4">
            <Avatar className="w-14 h-14 flex-shrink-0">
              <AvatarFallback className="bg-primary/10 text-primary text-lg font-bold">{selectedCompany.initials}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-display font-bold text-foreground">{selectedCompany.name}</h2>
                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-chart-5/10 text-chart-5 font-bold uppercase">{selectedCompany.industry}</span>
                <span className="flex items-center gap-0.5 text-xs text-muted-foreground ml-auto">
                  <Star className="w-3.5 h-3.5 text-chart-4 fill-chart-4" /> {selectedCompany.rating}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><Users className="w-3 h-3" /> {selectedCompany.employees} employees</p>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{selectedCompany.bio}</p>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={() => toggleFollow(selectedCompany.id, selectedCompany.name)}
            className={`flex-1 h-9 rounded-xl text-xs font-semibold transition-colors flex items-center justify-center gap-1 ${
            isFollowed ? "bg-secondary text-secondary-foreground" : "bg-primary text-primary-foreground hover:bg-primary/90"}`
            }>
              {isFollowed ? "✓ Following" : "Follow"}
            </button>
            {isFollowed &&
            <button onClick={(e) => toggleNotification(selectedCompany.id, e)}
            className={`h-9 w-9 rounded-xl flex items-center justify-center transition-colors ${
            notifications[selectedCompany.id] ? "bg-secondary/10 text-secondary" : "bg-muted text-muted-foreground"}`
            }>
                {notifications[selectedCompany.id] ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
              </button>
            }
          </div>
        </div>

        <div className="stat-card !p-4">
          <h3 className="text-sm font-display font-bold text-foreground mb-3 flex items-center gap-2">
            <Calendar className="w-4 h-4 text-secondary" /> Events
          </h3>
          {selectedCompany.events.length === 0 ?
          <p className="text-xs text-muted-foreground text-center py-4">No upcoming events</p> :

          <div className="space-y-2">
              {selectedCompany.events.map((event, i) =>
            <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-muted/50 hover:bg-muted transition-colors">
                  <div className="w-11 h-11 rounded-xl bg-secondary/10 flex flex-col items-center justify-center flex-shrink-0">
                    <Calendar className="w-3.5 h-3.5 text-secondary mb-0.5" />
                    <span className="text-[8px] font-bold text-secondary">{event.date.split(",")[0].split(" ").slice(0, 2).join(" ")}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <h4 className="text-sm font-semibold text-foreground">{event.title}</h4>
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-chart-5/10 text-chart-5 font-bold uppercase">{event.type}</span>
                    </div>
                    <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                      <MapPin className="w-3 h-3" />{event.location} · {event.attendees} attending
                    </p>
                  </div>
                  <button className="px-3 py-1.5 rounded-xl bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors flex-shrink-0">
                    Attend
                  </button>
                </div>
            )}
            </div>
          }
        </div>
      </div>);

  }

  // List View
  return (
    <div className="space-y-4">
      <div className="stat-card !p-4 space-y-3">
        <div className="flex items-center gap-2">
          <div className="flex gap-1 p-1 rounded-xl bg-muted w-fit">
            <button onClick={() => setSearchMode("companies")}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${searchMode === "companies" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"}`}>
              Companies
            </button>
            <button onClick={() => setSearchMode("events")}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${searchMode === "events" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"}`}>
              Events
            </button>
            <button onClick={() => setSearchMode("my")}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${searchMode === "my" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"}`}>
              My Companies
            </button>
          </div>
          <button onClick={() => setShowRecommended(!showRecommended)}
          className={`ml-auto flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-medium transition-colors ${
          showRecommended ? "bg-secondary/15 text-secondary" : "bg-muted text-muted-foreground hover:text-foreground"}`
          }>
            <TrendingUp className="w-3 h-3" /> Recommended
          </button>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={searchMode === "companies" ? "Search companies..." : "Search company events..."}
          className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-muted border-0 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
          
        </div>

        <div className="flex flex-wrap gap-2">
          <div className="relative">
            <select value={selectedIndustry} onChange={(e) => setSelectedIndustry(e.target.value)}
            className="appearance-none pl-3 pr-8 py-2 rounded-xl bg-muted text-xs font-medium text-foreground cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring">
              {industries.map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
            <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground pointer-events-none" />
          </div>
          {searchMode === "events" &&
          <div className="relative">
              <select value={selectedEventType} onChange={(e) => setSelectedEventType(e.target.value)}
            className="appearance-none pl-3 pr-8 py-2 rounded-xl bg-muted text-xs font-medium text-foreground cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring">
                {eventTypes.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground pointer-events-none" />
            </div>
          }
        </div>

        <p className="text-xs text-muted-foreground">
          {searchMode === "companies" ? `${filtered.length} compan${filtered.length !== 1 ? "ies" : "y"}` : `${filteredEvents.length} event${filteredEvents.length !== 1 ? "s" : ""}`}
        </p>
      </div>

      {searchMode === "events" ?
      <div className="space-y-3">
          {filteredEvents.length === 0 ?
        <div className="stat-card !p-8 text-center">
              <Calendar className="w-10 h-10 text-muted-foreground/20 mx-auto mb-3" />
              <p className="text-sm font-medium text-muted-foreground">No events found</p>
            </div> :

        filteredEvents.map((event, i) =>
        <div key={i} className="stat-card !p-4 flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl bg-secondary/10 flex flex-col items-center justify-center flex-shrink-0">
                  <Calendar className="w-4 h-4 text-secondary mb-0.5" />
                  <span className="text-[9px] font-bold text-secondary">{event.date.split(",")[0].split(" ").slice(0, 2).join(" ")}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <h4 className="text-sm font-display font-bold text-foreground">{event.title}</h4>
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-chart-5/10 text-chart-5 font-bold uppercase">{event.type}</span>
                  </div>
                  <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <button onClick={() => setSelectedCompanyId(event.companyId)} className="px-1.5 py-0.5 rounded-md bg-primary/10 text-primary font-medium hover:bg-primary/20 transition-colors">{event.companyName}</button>
                    <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{event.location}</span>
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">{event.date} · {event.attendees} attending</p>
                </div>
                <button className="px-3 py-1.5 rounded-xl bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors flex-shrink-0">
                  Attend
                </button>
              </div>
        )
        }
        </div> :

      <div className="space-y-3">
          {filtered.map((company) => {
          const isFollowed = following.includes(company.id);
          return (
            <div key={company.id} className="stat-card !p-4">
                <div className="flex items-start gap-3">
                  <button onClick={() => setSelectedCompanyId(company.id)}>
                    <Avatar className="w-12 h-12 flex-shrink-0">
                      <AvatarFallback className="bg-primary/10 text-primary text-sm font-bold">{company.initials}</AvatarFallback>
                    </Avatar>
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <button onClick={() => setSelectedCompanyId(company.id)} className="text-sm font-display font-bold text-foreground hover:text-secondary transition-colors">{company.name}</button>
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-chart-5/10 text-chart-5 font-bold uppercase">{company.industry}</span>
                      <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground ml-auto">
                        <Star className="w-3 h-3 text-chart-4 fill-chart-4" /> {company.rating}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed line-clamp-2">{company.bio}</p>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-2">
                      <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {company.employees}</span>
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {company.events.length} events</span>
                      {company.recentPosts > 0 &&
                    <span className="flex items-center gap-1 text-secondary"><Globe className="w-3 h-3" /> {company.recentPosts} new</span>
                    }
                    </div>
                    {company.events.length > 0 &&
                  <div className="mt-3 space-y-1.5">
                        {company.events.slice(0, 2).map((event, i) =>
                    <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-muted/50 text-xs">
                            <Calendar className="w-3 h-3 text-secondary flex-shrink-0" />
                            <span className="font-medium text-foreground truncate flex-1">{event.title}</span>
                            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-chart-5/10 text-chart-5 font-medium">{event.type}</span>
                            <span className="text-muted-foreground text-[10px] flex-shrink-0">{event.date.split(",")[0]}</span>
                          </div>
                    )}
                      </div>
                  }
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
                  <button onClick={() => toggleFollow(company.id, company.name)}
                className={`flex-1 h-8 rounded-xl text-xs font-semibold transition-colors flex items-center justify-center gap-1 ${
                isFollowed ? "bg-secondary text-secondary-foreground" : "bg-primary text-primary-foreground hover:bg-primary/90"}`
                }>
                    {isFollowed ? "✓ Following" : "Follow"}
                  </button>
                  {isFollowed &&
                <button onClick={(e) => toggleNotification(company.id, e)}
                className={`h-8 w-8 rounded-xl flex items-center justify-center transition-colors ${
                notifications[company.id] ? "bg-secondary/10 text-secondary" : "bg-muted text-muted-foreground"}`
                }>
                      {notifications[company.id] ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />}
                    </button>
                }
                  <button onClick={() => setSelectedCompanyId(company.id)}
                className="h-8 px-3 rounded-xl bg-muted text-xs font-medium text-foreground hover:bg-muted/80 transition-colors flex items-center gap-1">
                    <ExternalLink className="w-3 h-3" /> View
                  </button>
                </div>
              </div>);

        })}
          {filtered.length === 0 &&
        <div className="stat-card !p-8 text-center">
              <Building2 className="w-10 h-10 text-muted-foreground/20 mx-auto mb-3" />
              <p className="text-sm font-medium text-muted-foreground">No companies found</p>
            </div>
        }
        </div>
      }

      {/* My Companies View */}
      {searchMode === "my" &&
      <div className="space-y-3">
          {(() => {
          const myCompanies = allCompanies.filter((c) => following.includes(c.id));
          const filteredMy = myCompanies.filter((c) => c.name.toLowerCase().includes(searchQuery.toLowerCase()));
          if (filteredMy.length === 0) return (
            <div className="stat-card !p-8 text-center">
                <Briefcase className="w-10 h-10 text-muted-foreground/20 mx-auto mb-3" />
                <p className="text-sm font-medium text-muted-foreground">You're not following any companies yet</p>
              </div>);

          return filteredMy.map((company) =>
          <div key={company.id} className="stat-card !p-4">
                <div className="flex items-start gap-3">
                  <button onClick={() => setSelectedCompanyId(company.id)}>
                    <Avatar className="w-11 h-11 flex-shrink-0">
                      <AvatarFallback className="bg-primary/10 text-primary text-sm font-bold">{company.initials}</AvatarFallback>
                    </Avatar>
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <button onClick={() => setSelectedCompanyId(company.id)} className="text-sm font-display font-bold text-foreground hover:text-secondary transition-colors">{company.name}</button>
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-secondary/15 text-secondary font-bold">Following</span>
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-chart-5/10 text-chart-5 font-bold uppercase ml-auto">{company.industry}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{company.employees} employees · {company.events.length} events</p>
                    {company.events.length > 0 &&
                <div className="mt-2 space-y-1">
                        {company.events.slice(0, 2).map((event, i) =>
                  <div key={i} className="flex items-center gap-2 p-1.5 rounded-lg bg-muted/50 text-xs">
                            <Calendar className="w-3 h-3 text-secondary flex-shrink-0" />
                            <span className="font-medium text-foreground truncate flex-1">{event.title}</span>
                            <span className="text-muted-foreground text-[10px]">{event.date.split(",")[0]}</span>
                          </div>
                  )}
                      </div>
                }
                  </div>
                  <button onClick={(e) => toggleNotification(company.id, e)}
              className={`h-8 w-8 rounded-xl flex items-center justify-center transition-colors flex-shrink-0 ${
              notifications[company.id] ? "bg-secondary/10 text-secondary" : "bg-muted text-muted-foreground"}`
              }>
                    {notifications[company.id] ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
          );
        })()}
        </div>
      }
    </div>);

};

export default CompaniesDiscovery;